import InsightLayout from "../../components/InsightLayout";

const SUBTITLE = `Die meisten Menschen kennen KI als Chat-Fenster im Browser. Die produktivste Form von KI sieht aber anders aus: Sie lebt im Terminal und im Code-Editor, hat Zugriff auf Dateien und Systeme, und arbeitet agentisch — also mehrere Schritte selbständig, statt nur auf einzelne Fragen zu antworten. Claude Code, Cursor, Aider, Antigravity. Ich nutze diese Werkzeuge täglich, auch für nicht-technische Aufgaben. Hier ist, was sie unterscheidet, für wen sie sich lohnen — und warum das nicht nur ein Entwickler-Thema ist.`;

const CliAiTools = () => {
  return (
    <InsightLayout
      title="CLI-AI-Tools im Vergleich: Claude Code, Cursor, Aider, Antigravity — wann welches?"
      subtitle={SUBTITLE}
      metaDescription="Claude Code, Cursor, Aider, Antigravity im Vergleich: was sie unterscheidet, für wen sie sich lohnen, und welche Governance-Regeln Sie vorher brauchen."
      publishedDate="2026-05-26"
      readingTime="10 Min Lesezeit"
      category="AI in der Praxis"
    >
      <p className="text-lg">
        Wenn die meisten Menschen an KI denken, denken sie an ein Chat-Fenster
        im Browser: Frage rein, Antwort raus. Das ist die sichtbarste, aber
        bei weitem nicht die produktivste Form von KI. Die produktivste lebt im
        Terminal und im Code-Editor — sie hat Zugriff auf Dateien, Systeme und
        Werkzeuge, und sie arbeitet <strong>agentisch</strong>: mehrere
        Schritte selbständig, statt nur auf Einzelfragen zu antworten.
      </p>

      <p>
        Ich nutze diese Werkzeuge täglich — und zwar nicht nur für Code,
        sondern für Recherche, Datenanalyse, Dokumenten-Verarbeitung und
        Automatisierung. Dieser Artikel erklärt die wichtigsten CLI- und
        Agent-Tools, was sie unterscheidet, und für wen im Mittelstand sie
        sich lohnen. Vorweg: Das ist kein reines Entwickler-Thema.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Was Agent-Tools von Chat-KI unterscheidet
      </h2>

      <p>
        Der Unterschied ist kategorial. Ein Chat-LLM beantwortet eine Frage.
        Ein Agent-Tool bekommt ein <em>Ziel</em> und arbeitet eigenständig die
        nötigen Schritte ab — es liest Dateien, führt Befehle aus, prüft
        Ergebnisse, korrigiert sich, wiederholt. Während der Chat Ihnen sagt{" "}
        <em>wie</em> Sie etwas tun könnten, <em>tut</em> der Agent es.
      </p>

      <p>
        Konkret: Statt „Wie konvertiere ich diese 200 PDFs in strukturierte
        Daten?" zu fragen und die Anleitung selbst umzusetzen, geben Sie einem
        Agent-Tool die Aufgabe — und es liest die Dateien, schreibt das nötige
        Skript, führt es aus und liefert das Ergebnis. Das ist der Sprung von
        „KI als Ratgeber" zu „KI als ausführende Kraft".
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Die wichtigsten Werkzeuge im Überblick
      </h2>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        Claude Code (Anthropic)
      </h3>
      <p>
        Ein Terminal-basierter Agent von Anthropic. Stärke: tiefes Verständnis
        großer, zusammenhängender Aufgaben, exzellentes Datei- und
        System-Handling, Anbindung externer Werkzeuge über MCP (siehe{" "}
        <a href="/insights/mcp-erklaert" className="text-[#C5A66A] underline">
          MCP-Artikel
        </a>
        ). Es ist das Werkzeug, mit dem ich die meisten meiner eigenen
        Automatisierungen baue — auch diese Website-Inhalte entstehen in diesem
        Workflow. Eignet sich für komplexe, mehrstufige Aufgaben, nicht nur
        Code.
      </p>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        Cursor
      </h3>
      <p>
        Ein KI-nativer Code-Editor (auf Basis von VS Code). Stärke: die
        visuelle, integrierte Arbeitsumgebung — man sieht Änderungen direkt im
        Editor, kann sie Schritt für Schritt annehmen oder ablehnen. Ideal für
        alle, die ohnehin in einem Editor arbeiten und eine grafische
        Oberfläche dem reinen Terminal vorziehen. Die natürliche Wahl für
        interne Entwicklungs-Teams.
      </p>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        Aider
      </h3>
      <p>
        Ein schlankes, Open-Source-Terminal-Tool, das mit verschiedenen
        Modellen funktioniert (inklusive Open-Weights-Modellen). Stärke:
        Modell-Flexibilität und enge Git-Integration. Für technisch versierte
        Anwender, die volle Kontrolle und Modell-Unabhängigkeit wollen — und
        die Variante, die sich gut mit selbst gehosteten Modellen kombinieren
        lässt.
      </p>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        Antigravity (Google)
      </h3>
      <p>
        Googles agentisches CLI-Tool auf Gemini-Basis, vergleichbar mit Claude
        Code. Stärke: das große Kontextfenster der Gemini-Modelle und die
        Möglichkeit, mehrere Subagenten parallel arbeiten zu lassen. Ich nutze
        es selbst gezielt als <em>zweite Modell-Perspektive</em> — wenn ich
        eine unabhängige Sicht auf eine Recherche oder Entscheidung will, die
        nicht von meinem primären (Claude-basierten) Workflow gefärbt ist.
        Modell-Diversität als Qualitätshebel.
      </p>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        GitHub Copilot
      </h3>
      <p>
        Der etablierte Platzhirsch für Code-Vervollständigung, inzwischen mit
        Agent-Modus. Stärke: nahtlose Integration in bestehende
        Microsoft/GitHub-Umgebungen. Für Unternehmen, die ohnehin tief im
        Microsoft-Ökosystem sind, der Pfad des geringsten Widerstands.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Welches Werkzeug für wen?
      </h2>

      <div className="overflow-x-auto my-8">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-[#0F1B2D] text-white">
            <tr>
              <th className="px-4 py-3 text-left">Profil</th>
              <th className="px-4 py-3 text-left">Empfehlung</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            <tr>
              <td className="px-4 py-3 font-medium">Komplexe, mehrstufige Aufgaben (auch nicht-Code)</td>
              <td className="px-4 py-3">Claude Code</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium">Internes Dev-Team mit Editor-Workflow</td>
              <td className="px-4 py-3">Cursor</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium">Modell-Unabhängigkeit + Self-Hosting</td>
              <td className="px-4 py-3">Aider</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium">Zweite Modell-Perspektive / großer Kontext</td>
              <td className="px-4 py-3">Antigravity</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium">Tiefe Microsoft/GitHub-Integration</td>
              <td className="px-4 py-3">GitHub Copilot</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Warum das kein reines Entwickler-Thema ist
      </h2>

      <p>
        Der verbreitetste Irrtum: „Das sind Tools für Programmierer." Stimmt
        historisch, aber nicht mehr. Diese Werkzeuge können jede Aufgabe
        übernehmen, die sich in Schritte zerlegen und mit Dateien oder Systemen
        ausführen lässt:
      </p>

      <ul className="list-disc pl-6 space-y-2 my-4">
        <li>
          <strong>Daten-Aufbereitung:</strong> 500 Excel-Dateien
          zusammenführen, bereinigen, auswerten — eine Aufgabe, für die früher
          ein Mitarbeiter Tage brauchte.
        </li>
        <li>
          <strong>Dokumenten-Verarbeitung:</strong> Verträge analysieren,
          Fristen extrahieren, Zusammenfassungen erstellen.
        </li>
        <li>
          <strong>Recherche + Synthese:</strong> Mehrere Quellen parallel
          durchsuchen und zu einem strukturierten Bericht verdichten.
        </li>
        <li>
          <strong>Automatisierung:</strong> Wiederkehrende Workflows als
          Skript bauen lassen, das dann eigenständig läuft.
        </li>
      </ul>

      <p>
        <strong>Nachtrag August 2026:</strong> Diese Entwicklung hat inzwischen
        das Terminal verlassen. Die großen Assistenten liefern seit Sommer 2026
        fertige Arbeitsergebnisse direkt aus der gewohnten Oberfläche, ohne
        Kommandozeile. Was das für Delegation und Abnahme bedeutet, steht im
        Artikel über{" "}
        <a
          href="/insights/arbeitsagenten-statt-chat"
          className="text-[#C5A66A] underline"
        >
          Arbeitsagenten
        </a>
        .
      </p>

      <p>
        Die Einstiegshürde ist die Terminal-Scheu. Aber genau die löst sich
        auf, weil man dem Werkzeug in normaler Sprache sagt, was zu tun ist.
        Wer einmal erlebt hat, wie eine 3-Tage-Aufgabe in 20 Minuten erledigt
        ist, denkt anders über „KI für Nicht-Techniker".
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Die Governance-Seite — was Sie regeln müssen
      </h2>

      <p>
        Mit ausführender KI steigen die Risiken. Bevor Sie Agent-Tools im
        Unternehmen breit einsetzen, brauchen Sie drei Regeln:
      </p>

      <p>
        <strong>Erstens — Berechtigungs-Scope.</strong> Agent-Tools können
        Dateien ändern, Befehle ausführen, Daten löschen. Sie müssen klar
        definieren, worauf ein Werkzeug zugreifen darf — und worauf nicht.
        Dieselbe Least-Privilege-Logik wie bei{" "}
        <a href="/insights/mcp-erklaert" className="text-[#C5A66A] underline">
          MCP
        </a>
        .
      </p>

      <p>
        <strong>Zweitens — Freigabe-Schritte für kritische Aktionen.</strong>{" "}
        Gute Tools fragen vor riskanten Operationen (Dateien löschen, Daten
        nach außen senden) nach Bestätigung. Diese Schutzschicht sollte
        aktiviert bleiben, gerade in der Einführungsphase.
      </p>

      <p>
        <strong>Drittens — Datenklassen-Bewusstsein.</strong> Welche Modelle
        bekommen welche Daten zu sehen? Ein Cloud-Tool mit sensiblen
        Personaldaten zu füttern, ist dieselbe DSGVO-Frage wie bei jedem
        anderen KI-Einsatz (siehe{" "}
        <a href="/insights/dsgvo-art-35-ki" className="text-[#C5A66A] underline">
          DSGVO-Artikel
        </a>
        ).
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Die praktische Empfehlung
      </h2>

      <p>
        Für den Einstieg im Mittelstand: Beginnen Sie mit einer Person, die
        technik-affin, aber kein Vollzeit-Entwickler sein muss — oft jemand aus
        Controlling, Operations oder einem Innovation-Team. Geben Sie dieser
        Person ein Agent-Tool (Claude Code oder Cursor) und ein konkretes,
        wiederkehrendes Problem. Innerhalb weniger Wochen entsteht so ein
        interner Multiplikator, der zeigt, was möglich ist — und das überzeugt
        mehr als jede Präsentation.
      </p>

      <p>
        Der Fehler, den ich am häufigsten sehe: Diese Werkzeuge als reines
        IT-Thema zu behandeln und in der IT-Abteilung zu lassen. Der eigentliche
        Hebel liegt in den Fachabteilungen — bei den Menschen, die ihre
        wiederkehrenden, zeitfressenden Aufgaben am besten kennen. Geben Sie
        ihnen das Werkzeug, nicht nur den Entwicklern.
      </p>

      <p className="mt-12 text-sm text-gray-600 italic">
        Stand: 26. Mai 2026. Die Tool-Landschaft entwickelt sich schnell —
        Funktionsumfang und Verfügbarkeit ändern sich laufend. Verwandte
        Artikel:{" "}
        <a href="/insights/mcp-erklaert" className="text-[#C5A66A] underline">
          MCP erklärt
        </a>{" "}
        ·{" "}
        <a
          href="/insights/llm-tool-landschaft-2026"
          className="text-[#C5A66A] underline"
        >
          LLM-Tool-Landschaft 2026
        </a>{" "}
        ·{" "}
        <a
          href="/insights/modell-landschaft-sommer-2026"
          className="text-[#C5A66A] underline"
        >
          Das Modell-Beben im Sommer 2026
        </a>
        .
      </p>
    </InsightLayout>
  );
};

export default CliAiTools;
